/**
 * ProseMirrorPortal: Creates the React portal and renders the editor UI
 *
 * Why use a portal?
 * - ProseMirror manages its own DOM directly (not through React)
 * - React's reconciliation would conflict with ProseMirror's DOM management
 * - The portal isolates ProseMirror's DOM from React's reconciliation
 */

import clsx from "clsx";
import { get } from "lodash-es";
import Delta from "quill-delta";
import { FC, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import { ProseMirrorEditorInstance } from "./prosemirror/editor-instance";
import type { ProseMirrorEditorInstanceOptions, ProseMirrorPortalProps } from "./prosemirror/types";

import { useCapabilities } from "store/capability-hooks";
import { USER_INPUT_EVENT_CATEGORY, addTrackingToEvent, getPageFieldProps } from "utils/amplitude";

/**
 * ProseMirrorPortal renders the editor UI through a React portal
 *
 * This component:
 * 1. Uses the container element provided by the parent component
 * 2. Creates a ProseMirrorEditorInstance and attaches it to a div inside the portal
 * 3. Recreates the editor instance when readOnly changes (since editable is set at init)
 * 4. Cleans up the editor instance on unmount
 */
export const ProseMirrorPortal: FC<ProseMirrorPortalProps> = ({
  containerElement,
  className = "",
  readOnly,
  doc,
  path,
  fieldPath,
  userId,
  presenceViewType,
  users,
  eventName,
  pageId,
  embedded = false,
  create = false,
  showPlaceholder = false,
  placeHolderText = "",
  characterCounter,
  maxLength,
  isAuthoringPage,
  onEmbedURLFromPaste,
  onEmbedURLFromLink,
}) => {
  const rootElementRef = useRef<HTMLDivElement | null>(null);
  const editorInstanceRef = useRef<ProseMirrorEditorInstance | null>(null);
  const { canAuthorSection } = useCapabilities();
  const hasContainer = containerElement !== null;

  useEffect(() => {
    if (!containerElement || !rootElementRef.current) return;

    const options: ProseMirrorEditorInstanceOptions = {
      doc,
      path,
      readOnly,
      userId,
      canAuthorSection,
      presenceViewType,
      users,
      embedded,
      create,
      pageId,
      showPlaceholder,
      placeHolderText,
      characterCounter,
      fieldPath,
      maxLength,
      isAuthoringPage,
      onEmbedURLFromPaste,
      onEmbedURLFromLink,
    };
    const instance = new ProseMirrorEditorInstance(rootElementRef.current, options);
    editorInstanceRef.current = instance;

    const editorElement = rootElementRef.current;
    const editorWrapper = editorElement.closest(".prosemirror-editor-wrapper");
    const trackedFocus = addTrackingToEvent({
      eventName: eventName || "Free response block selected",
      eventProperties: { ...getPageFieldProps(pageId, path), ...USER_INPUT_EVENT_CATEGORY },
    });

    // The browser can natively focus a contenteditable element for clicks that land
    // outside its bounding box, with no JS involvement. We detect this by tracking
    // whether the preceding mousedown landed inside the editor wrapper. The spurious
    // focusin fires synchronously as part of the mousedown's default action, so we
    // reset the flag via setTimeout(0) — after the current event dispatch completes
    // but before any keyboard or programmatic focus can arrive.
    let shouldAcceptFocus = true;
    const handleDocMouseDown = (e: MouseEvent) => {
      shouldAcceptFocus = !!editorWrapper?.contains(e.target as Node);
      setTimeout(() => {
        shouldAcceptFocus = true;
      }, 0);
    };
    document.addEventListener("mousedown", handleDocMouseDown, true);

    const handleFocus = (e: Event) => {
      if (!shouldAcceptFocus) {
        (document.activeElement as HTMLElement | null)?.blur();
        return;
      }
      trackedFocus(e);
    };
    const handleToolbarMouseDown = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest(".ProseMirror-menubar")) {
        e.preventDefault();
      }
    };
    editorElement.addEventListener("focusin", handleFocus);
    editorElement.addEventListener("mousedown", handleToolbarMouseDown);

    return () => {
      document.removeEventListener("mousedown", handleDocMouseDown, true);
      editorElement.removeEventListener("focusin", handleFocus);
      editorElement.removeEventListener("mousedown", handleToolbarMouseDown);
      instance.destroy();
      editorInstanceRef.current = null;
    };
    // We should take care to avoid recreating the editor unnecessarily.
    // Dependencies not included: containerElement, doc, path, fieldPath, userId, users, pageId,
    // eventName, className, embedded, create, placeHolderText, characterCounter, maxLength,
    // isAuthoringPage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasContainer, readOnly, presenceViewType, showPlaceholder, canAuthorSection]);

  // Timeline view replaces doc between renders, so we pull the new content here
  // Also, ShareDB's automatic error recovery will re-fetch the doc data entirely on error,
  // which this file needs to react to by detecting changes in doc.data.
  useEffect(() => {
    if (editorInstanceRef.current) {
      const data = get(doc.data, path) as Delta;
      editorInstanceRef.current.syncContentsFromDoc(data, path);
    }
  }, [doc, path, doc?.data]);

  useEffect(() => {
    if (editorInstanceRef.current) {
      editorInstanceRef.current.setIsAuthoringPage(isAuthoringPage ?? false);
    }
  }, [isAuthoringPage]);

  useEffect(() => {
    if (editorInstanceRef.current) {
      editorInstanceRef.current.updateCharacterCountLimit(maxLength || null);
    }
  }, [maxLength]);

  useEffect(() => {
    if (editorInstanceRef.current) {
      editorInstanceRef.current.updateCharacterCountMode(characterCounter);
    }
  }, [characterCounter]);

  useEffect(() => {
    if (editorInstanceRef.current) {
      editorInstanceRef.current.updateOnEmbedURLFromPaste(onEmbedURLFromPaste);
    }
  }, [onEmbedURLFromPaste]);

  useEffect(() => {
    if (editorInstanceRef.current) {
      editorInstanceRef.current.updateOnEmbedURLFromLink(onEmbedURLFromLink);
    }
  }, [onEmbedURLFromLink]);

  if (!containerElement) {
    return null;
  }

  return createPortal(
    <div
      className={clsx("prosemirror-editor-wrapper", className, {
        "textfield-disabled": readOnly,
        embedded,
      })}
    >
      {/*
        This div is where ProseMirror EditorView will attach.
        The editor content is rendered directly by ProseMirror into this element.
        Because this is rendered via a portal, React won't try to reconcile
        the DOM that ProseMirror manages.
      */}
      <div className="prosemirror-editor" ref={rootElementRef} />
    </div>,
    containerElement,
  );
};
